import React, { useState, useEffect } from "react";
import api from "../api";
import Table from "../components/Table";
import Modal from "../components/Modal";
import ImageUpload from "../components/ImageUpload";
import { FiPlus } from "react-icons/fi";
import toast from "react-hot-toast";

const Colleges = () => {
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCollege, setCurrentCollege] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    website: "",
    googleMapLink: "",
    email: "",
    logo: "",
    departments: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchColleges();
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchColleges = async () => {
    try {
      const response = await api.get("/college/getall", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      setColleges(response.data.data);
      setPagination(prevState => ({
        ...prevState,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));
    } catch (error) {
      toast.error("Failed to fetch colleges");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prevState => ({ ...prevState, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prevState => ({ ...prevState, limit: newLimit, page: 1 }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prevState => ({ ...prevState, page: 1 }));
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = (urls) => {
    setFormData((prevState) => ({ ...prevState, logo: urls[0] }));
  };

  const handleLogoRemove = () => {
    setFormData((prevState) => ({ ...prevState, logo: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        departments: formData.departments.split(',').map(dept => dept.trim()),
      };
      if (currentCollege) {
        await api.put(`/college/${currentCollege._id}`, data);
        toast.success("College updated successfully");
      } else {
        await api.post("/college", data);
        toast.success("College created successfully");
      }
      setIsModalOpen(false);
      fetchColleges();
    } catch (error) {
      toast.error("Failed to save college");
    }
  };

  const handleEdit = (college) => {
    setCurrentCollege(college);
    setFormData({
      name: college.name,
      address: college.address,
      website: college.website,
      googleMapLink: college.googleMapLink,
      email: college.email,
      logo: college.logo,
      departments: college.departments.join(", "),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this college?")) {
      try {
        await api.delete(`/college/${id}`);
        toast.success("College deleted successfully");
        fetchColleges();
      } catch (error) {
        toast.error("Failed to delete college");
      }
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "address", label: "Address" },
    { key: "email", label: "Email" },
    {
      key: "logo",
      label: "Logo",
      render: (item) => (
        <img
          src={item.logo}
          alt={item.name}
          className="w-10 h-10 rounded-full"
        />
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-gray-900">Colleges</h1>
        <button
          onClick={() => {
            setCurrentCollege(null);
            setFormData({
              name: "",
              address: "",
              website: "",
              googleMapLink: "",
              email: "",
              logo: "",
              departments: "",
            });
            setIsModalOpen(true);
          }}
          className="px-4 py-2 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <FiPlus className="inline-block mr-2" />
          Add College
        </button>
      </div>
      <Table
        columns={columns}
        data={colleges}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSearch={handleSearch}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentCollege ? "Edit College" : "Add College"}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700"
              >
                Address
              </label>
              <input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label
                htmlFor="website"
                className="block text-sm font-medium text-gray-700"
              >
                Website
              </label>
              <input
                type="url"
                name="website"
                id="website"
                value={formData.website}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label
                htmlFor="googleMapLink"
                className="block text-sm font-medium text-gray-700"
              >
                Google Map Link
              </label>
              <input
                type="url"
                name="googleMapLink"
                id="googleMapLink"
                value={formData.googleMapLink}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label
                htmlFor="departments"
                className="block text-sm font-medium text-gray-700"
              >
                Departments (comma-separated)
              </label>
              <input
                type="text"
                name="departments"
                id="departments"
                value={formData.departments}
                onChange={handleInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Logo
              </label>
              <ImageUpload
                onUpload={handleLogoUpload}
                onRemove={handleLogoRemove}
                images={formData.logo ? [formData.logo] : []}
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              type="submit"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            >
              {currentCollege ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Colleges;
